import { FACTIONS } from "@/data";
import { selectActiveForce, selectTotalCost, useForceStore } from "@/store/forceStore";
import { PrintElementCard } from "./PrintElementCard";
import { VehicleStatCard } from "./VehicleStatCard";

interface PrintViewProps {
	onClose: () => void;
}

export function PrintView({ onClose }: PrintViewProps) {
	const force = useForceStore(selectActiveForce);
	const totalCost = useForceStore(selectTotalCost);

	if (!force) return null;

	const faction = FACTIONS.find((f) => f.id === force.faction);

	return (
		<div className="print-view bg-white text-black min-h-screen">
			{/* Screen-only buttons — responsive positioning */}
			<div className="print-hide fixed top-4 right-4 z-50 flex gap-2">
				<button
					onClick={() => window.print()}
					className="bg-blue-600 text-white px-3 py-2 lg:px-4 rounded-lg shadow-lg hover:bg-blue-500 transition-colors text-sm font-medium min-h-[44px]"
				>
					Print / Save PDF
				</button>
				<button
					onClick={onClose}
					className="bg-gray-800 text-white px-3 py-2 lg:px-4 rounded-lg shadow-lg hover:bg-gray-700 transition-colors text-sm font-medium min-h-[44px]"
				>
					Close
				</button>
			</div>

			<div className="w-full max-w-[210mm] mx-auto px-4 py-6 lg:px-6 lg:py-8">
				{/* Force Header */}
				<div className="border-b-2 border-gray-900 pb-3 mb-6">
					<h1 className="text-xl lg:text-2xl font-bold text-gray-900 uppercase tracking-wider">
						{force.name || "Unnamed Force"}
					</h1>
					<div className="flex flex-wrap items-center gap-2 lg:gap-4 mt-1 text-sm text-gray-600">
						<span>{faction?.name ?? force.faction}</span>
						<span className="font-mono font-bold text-gray-900">{totalCost}r</span>
						{force.maxPoints > 0 && (
							<span className="text-gray-400">/ {force.maxPoints}r</span>
						)}
					</div>
				</div>

				{/* Elements */}
				{force.elements.length > 0 && (
					<div className="mb-6">
						<h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide border-b border-gray-300 pb-1 mb-3">
							Elements
						</h2>
						{force.elements.map((element) => (
							<PrintElementCard key={element.id} element={element} />
						))}
					</div>
				)}

				{/* Vehicles */}
				{force.vehicles.length > 0 && (
					<div className="mb-6">
						<h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide border-b border-gray-300 pb-1 mb-3">
							Vehicles
						</h2>
						{force.vehicles.map((vehicle) => (
							<VehicleStatCard key={vehicle.id} vehicle={vehicle} />
						))}
					</div>
				)}

				{/* Empty state */}
				{force.elements.length === 0 && force.vehicles.length === 0 && (
					<div className="text-center text-gray-400 py-20">
						<p className="text-lg">No units in this force yet.</p>
						<p className="text-sm mt-2">Add elements and vehicles, then return to print view.</p>
					</div>
				)}
			</div>
		</div>
	);
}
